/**
 * Portfolio Card Component
 * TASK-210: Display portfolio summary in card format
 * TASK-263: Updated to accept flexible portfolio type (works with API and mock data)
 * TASK-321: Added sync status badge for Pretim integration
 *
 * Shows portfolio name, custodian, value, type, sync status, and actions
 */

import React from 'react';
import {
  Building2,
  RefreshCw,
  Eye,
  MoreVertical,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Upload,
  X
} from 'lucide-react';
import { MockManualAsset } from '../../services/mock/portfolioMockData';
import SyncStatusBadge, { SyncStatus } from './atoms/SyncStatusBadge';

/**
 * TASK-263/TASK-321: Flexible portfolio display type
 * Works with both MockPortfolio and EnrichedPortfolio from API, includes sync fields
 */
interface PortfolioCardData {
  id: string;
  portfolioName: string;
  portfolioType: 'manual' | 'api';
  status: 'active' | 'closed';
  custodian?: string;  // Optional - may be undefined for API portfolios
  currency: string;
  currencyBase?: string;  // Fallback for currency
  totalValue: number;
  totalSecurities: number;
  totalCash: number;
  totalAlternativeAssets: number;
  numberOfPositions: number;
  accountNumber?: string;
  apiConnectionStatus?:
    | 'connected'
    | 'disconnected'
    | 'syncing'
    | 'error'
    | 'never_synced'
    | 'partial'
    | 'failed'
    | 'pending'
    | 'running';
  syncEnabled?: boolean;
  lastSyncDate?: string;
  manualAssets?: MockManualAsset[];  // Use MockManualAsset type directly
  // TASK-321: Sync configuration fields
  dataSource?: 'manual' | 'pretim' | 'csv_import';
  externalId?: string;
  lastSyncStatus?: 'success' | 'partial' | 'failed' | 'pending' | 'running';
  // Pretim v1.1 metadata
  portfolioNumber?: string;
  wealthManagerName?: string;
}

interface PortfolioCardProps {
  portfolio: PortfolioCardData;
  onView?: (portfolioId: string) => void;
  onSync?: (portfolioId: string) => void;
  onEdit?: (portfolioId: string) => void;
  onAddAsset?: (portfolioId: string) => void;
  onUploadFile?: (portfolioId: string) => void;
  onClosePortfolio?: (portfolioId: string) => void;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({
  portfolio,
  onView,
  onSync,
  onEdit,
  onAddAsset,
  onUploadFile,
  onClosePortfolio
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false);
  // Format currency
  const formatCurrency = (value: number, currency: string = 'CHF') => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Get custodian color
  const getCustodianColor = (custodian: string) => {
    const colors: Record<string, string> = {
      'UBS': 'from-red-500 to-red-600',
      'Credit Suisse': 'from-blue-500 to-blue-600',
      'Julius Baer': 'from-orange-500 to-orange-600',
      'Pictet': 'from-green-500 to-green-600',
      'Vontobel': 'from-purple-500 to-purple-600',
      'None': 'from-gray-500 to-gray-600'
    };
    return colors[custodian] || 'from-gray-500 to-gray-600';
  };

  // Get sync status
  const getSyncStatus = () => {
    if (!portfolio.apiConnectionStatus) return null;

    const statusConfig = {
      connected: {
        icon: CheckCircle2,
        color: 'text-green-600',
        bg: 'bg-green-50',
        label: 'Connected'
      },
      syncing: {
        icon: RefreshCw,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        label: 'Syncing...'
      },
      error: {
        icon: AlertCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        label: 'Error'
      },
      disconnected: {
        icon: AlertCircle,
        color: 'text-gray-600',
        bg: 'bg-gray-50',
        label: 'Disconnected'
      },
      // TASK-657: real sync-lifecycle states — a never-synced external
      // portfolio must never read as "Connected".
      never_synced: {
        icon: AlertCircle,
        color: 'text-status-unavailable-fg',
        bg: 'bg-status-unavailable/10',
        label: 'Never synced'
      },
      partial: {
        icon: AlertCircle,
        color: 'text-status-attention-fg',
        bg: 'bg-status-attention/10',
        label: 'Partial sync'
      },
      failed: {
        icon: AlertCircle,
        color: 'text-status-urgent-fg',
        bg: 'bg-status-urgent/10',
        label: 'Sync failed'
      },
      pending: {
        icon: RefreshCw,
        color: 'text-status-info-fg',
        bg: 'bg-status-info/10',
        label: 'Sync pending'
      },
      running: {
        icon: RefreshCw,
        color: 'text-status-info-fg',
        bg: 'bg-status-info/10',
        label: 'Syncing…'
      }
    };

    return statusConfig[portfolio.apiConnectionStatus];
  };

  const syncStatus = getSyncStatus();

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200">
      {/* Header with custodian gradient */}
      <div className={`h-2 rounded-t-lg bg-gradient-to-r ${getCustodianColor(portfolio.custodian || 'None')}`} />

      <div className="p-5">
        {/* Portfolio Name & Type Badge */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {portfolio.portfolioName}
            </h3>
            {portfolio.portfolioNumber && (
              <p className="text-xs text-gray-400 mb-0.5">No. {portfolio.portfolioNumber}</p>
            )}
            {portfolio.wealthManagerName && (
              <p className="text-xs text-gray-400 mb-0.5">WM: {portfolio.wealthManagerName}</p>
            )}
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">
                {portfolio.custodian ||
                  (portfolio.portfolioType === 'api' ? 'External source' : 'Kielis-managed')}
              </span>
              {portfolio.accountNumber && (
                <span className="text-sm text-gray-400">• {portfolio.accountNumber}</span>
              )}
            </div>
          </div>

          {/* TASK-321: Type and Sync Status Badges */}
          <div className="flex flex-col items-end gap-1">
            {/* Portfolio type badge */}
            <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              portfolio.portfolioType === 'api'
                ? 'bg-blue-50 text-blue-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {portfolio.portfolioType === 'api' ? 'Connected PMS' : 'Kielis-managed'}
            </div>

            {/* Sync status badge (for non-manual data sources) */}
            {portfolio.dataSource && portfolio.dataSource !== 'manual' && (
              <SyncStatusBadge
                status={(portfolio.lastSyncStatus ?? 'never') as SyncStatus}
                dataSource={portfolio.dataSource}
                lastSyncAt={portfolio.lastSyncDate}
                syncEnabled={portfolio.syncEnabled}
                size="sm"
              />
            )}
          </div>
        </div>

        {/* Total Value */}
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-1">Total Value</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(portfolio.totalValue, portfolio.currencyBase || portfolio.currency)}
          </div>
        </div>

        {/* Portfolio Composition */}
        <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-gray-100">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Securities</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatCurrency(portfolio.totalSecurities, portfolio.currencyBase || portfolio.currency)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Cash</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatCurrency(portfolio.totalCash, portfolio.currencyBase || portfolio.currency)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Positions</div>
            <div className="text-sm font-semibold text-gray-900">
              {portfolio.numberOfPositions}
            </div>
          </div>
        </div>

        {/* Alternative Assets if any */}
        {portfolio.manualAssets && portfolio.manualAssets.length > 0 && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Alternative Assets</div>
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(portfolio.totalAlternativeAssets, portfolio.currencyBase || portfolio.currency)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {portfolio.manualAssets.length} asset{portfolio.manualAssets.length !== 1 ? 's' : ''}
                </div>
              </div>
              {portfolio.portfolioType === 'manual' && onAddAsset && (
                <button
                  onClick={() => onAddAsset(portfolio.id)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add
                </button>
              )}
            </div>
          </div>
        )}

        {/* Upload/Add CTAs for manual portfolios */}
        {portfolio.portfolioType === 'manual' && (
          <div className="mb-4 pb-4 border-b border-gray-100 space-y-2">
            {/* Upload Positions Button */}
            {portfolio.numberOfPositions === 0 && onUploadFile && (
              <button
                onClick={() => onUploadFile(portfolio.id)}
                className="w-full py-2 px-3 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                📊 Upload Excel/CSV File
              </button>
            )}

            {/* Add Alternative Assets Button */}
            {(!portfolio.manualAssets || portfolio.manualAssets.length === 0) && onAddAsset && (
              <button
                onClick={() => onAddAsset(portfolio.id)}
                className="w-full py-2 px-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-sm text-gray-600 hover:text-blue-600 font-medium"
              >
                + Add Alternative Assets
              </button>
            )}
          </div>
        )}

        {/* Sync Status for API Portfolios */}
        {portfolio.portfolioType === 'api' && syncStatus && (
          <div className={`flex items-center gap-2 ${syncStatus.bg} px-3 py-2 rounded-md mb-4`}>
            <syncStatus.icon className={`w-4 h-4 ${syncStatus.color} ${
              portfolio.apiConnectionStatus === 'syncing' ? 'animate-spin' : ''
            }`} />
            <div className="flex-1">
              <div className={`text-xs font-medium ${syncStatus.color}`}>
                {syncStatus.label}
              </div>
              {portfolio.lastSyncDate && portfolio.apiConnectionStatus === 'connected' && (
                <div className="text-xs text-gray-500 mt-0.5">
                  Last sync: {formatDate(portfolio.lastSyncDate)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {onView && (
            <button
              onClick={() => onView(portfolio.id)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
          )}

          {portfolio.portfolioType === 'api' && portfolio.syncEnabled && onSync && (
            <button
              onClick={() => onSync(portfolio.id)}
              disabled={portfolio.apiConnectionStatus === 'syncing'}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sync Now"
            >
              <RefreshCw className={`w-4 h-4 ${
                portfolio.apiConnectionStatus === 'syncing' ? 'animate-spin' : ''
              }`} />
            </button>
          )}

          {(onEdit || onClosePortfolio) && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                title="More Options"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  />

                  {/* Dropdown Content */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {onEdit && (
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          onEdit(portfolio.id);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit Details
                      </button>
                    )}

                    {portfolio.portfolioType === 'manual' && onUploadFile && (
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          onUploadFile(portfolio.id);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Positions
                      </button>
                    )}

                    {onClosePortfolio && portfolio.status === 'active' && (
                      <>
                        <div className="border-t border-gray-200 my-1" />
                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            onClosePortfolio(portfolio.id);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Close Portfolio
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioCard;
export type { PortfolioCardData };
